import {Repository} from "../../persistence/repository";
import {IssuePersonalData} from "../entities/issue-personal-data.entity";
import {singleton} from "tsyringe";
import {persistence} from "../../decorators/persistence.decorator";
import {plainToInstance} from "class-transformer";
import {IssuePersonalDataDto} from "../dtos/issue-personal-data.dto";
import {Issue} from "../entities/issue.entity";
import {EntityState} from "../../persistence/entity-state.enum";
import {ChangeTracker} from "../../change-tracking/change-tracker";
import {ShortIdHelper} from "../../helpers/short-id.helper";
import {QueryParameter} from "../../connectivity/query-parameter.interface";
import {ArrayHelper} from "../../helpers/array.helper";

@singleton()
@persistence({entity: IssuePersonalData})
export class IssuePersonalDataRepository extends Repository<IssuePersonalData> {

    public create(issue: Issue): Promise<IssuePersonalData> {
        const issuePersonalData = plainToInstance(IssuePersonalData, <IssuePersonalDataDto>{
            uid: ShortIdHelper.generate(),
            issue: issue.uid,
            personalLabel: null,
            timeCluster: null,
            personalDate: null,
            sortOrder: 0,
        });

        const issuePersonalDataProxy = ChangeTracker.create(issuePersonalData);

        this.entityManager.setLoadedEntity(issuePersonalDataProxy, EntityState.NEW);

        return Promise.resolve(issuePersonalDataProxy);
    }

    public fromDto(dto: IssuePersonalDataDto): IssuePersonalData {
        const issuePersonalDataProxy = plainToInstance(IssuePersonalData, dto);

        this.entityManager.setLoadedEntity(issuePersonalDataProxy, EntityState.PERSISTED);

        return issuePersonalDataProxy;
    }

    /**
     * `'issue.uid'` is not a direct property of `IssuePersonalData` (the
     * property is `issue`, a lazily-resolved relation) — `Repository.findOneBy`
     * only accepts a direct `keyof T`, so this goes straight through
     * `entityManager.findAllByQueryParameters` instead, mirroring
     * `IssueRepository`'s own query-building pattern. The backend already
     * scopes the read to the caller's own row, so at most one result exists
     * for a given issue.
     */
    public async findOneByIssue(issueUid: string): Promise<IssuePersonalData | null> {
        const dtos = await this.entityManager.findAllByQueryParameters<IssuePersonalDataDto>(
            this.entity.name,
            [<QueryParameter<string>>{key: `issue.uid`, comparator: `eq`, value: issueUid}],
            [],
            1,
        );

        return this._transformDtoToEntity(dtos[0] ?? null);
    }

    /**
     * "My Organizer" personal-label columns (Anke, 2026-09-06, "issues per
     * drag&drop in die neue Spalte ziehen"): every row currently assigned to
     * ONE of the current User's own labels — the backend already scopes
     * `/query/issue-personal-data` reads to the caller's own rows
     * (`OwnerReadScopedInterface`), so this only adds the label filter on top.
     *
     * Sorted client-side by `sortOrder` ascending ("My Organizer" reorder step,
     * 2026-09-06) — a label column stays small (a personal todo list, not a
     * paginated view), so this is simpler than threading a server-side `orderBy`
     * through `entityManager.findAllByQueryParameters()` (which would touch a
     * method shared by every repository, for a benefit only this one call site
     * needs). Tie-break by `uid` — a PLAIN codepoint comparison, not
     * `localeCompare` (ICU collation order is locale/engine-dependent; a test
     * could pass while the real, browser-rendered order differs) — several rows
     * can share `sortOrder` (the `0` sentinel, or not yet individually reordered
     * after the migration backfill), so the order must stay deterministic.
     */
    public async findAllByPersonalLabel(labelUid: string): Promise<IssuePersonalData[]> {
        const dtos = await this.entityManager.findAllByQueryParameters<IssuePersonalDataDto>(
            this.entity.name,
            [<QueryParameter<string>>{key: `personalLabel.uid`, comparator: `eq`, value: labelUid}],
        );

        const entities = await Promise.all(dtos.map(dto => this._transformDtoToEntity(dto)));

        return entities
            .filter(ArrayHelper.notEmpty)
            .sort((a, b) => a.sortOrder - b.sortOrder || (a.uid < b.uid ? -1 : a.uid > b.uid ? 1 : 0));
    }

    /**
     * Every one of the current User's own `IssuePersonalData` rows,
     * regardless of label — used by `<my-organizer-assigned-column>` to
     * exclude Issues already dropped into ANY personal-label column (Anke,
     * 2026-09-06: "ein issue kann nur genau einer Spalte zugewiesen
     * werden"). No filter needed: same owner-scoping as
     * `PersonalLabelRepository.findAllMine()`.
     */
    public async findAllMine(): Promise<IssuePersonalData[]> {
        const dtos = await this.entityManager.findAllByQueryParameters<IssuePersonalDataDto>(this.entity.name, []);

        const entities = await Promise.all(dtos.map(dto => this._transformDtoToEntity(dto)));

        return entities.filter(ArrayHelper.notEmpty);
    }
}
